import { defaultLocale, translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import ServicePageTemplate from 'presentations/views/components/templates/ServicePageTemplate';
import Headline from 'presentations/views/components/atoms/Headline';
import ContactCompanyList from 'presentations/views/components/organisms/ContactCompanyList';
import Icon from 'presentations/views/components/atoms/Icon';
import Image from 'presentations/views/components/atoms/Image';
import styled from '@emotion/styled';
import { useContext } from 'react';
import { ServicePortalContext } from 'presentations/reducers/ServicePortal';
import {
  borderRadius,
  fontSize,
  fontWeight,
  mediaMax,
  spacing,
} from 'presentations/views/styles';
import {
  blueGrayPale,
  gray04,
  primary,
} from 'presentations/views/styles/color';
import {
  contactPath,
  pricingEducationWidgetPath,
  webPath,
} from 'presentations/routes/path';
import { useRouter } from 'next/router';
import Header from 'presentations/views/components/organisms/Header';
import MainDocument from 'presentations/views/components/MainDocument';

export default function Component() {
  const t = translateInComponent(translations);
  const { state: serviceState } = useContext(ServicePortalContext);
  const viewer = serviceState.viewer;

  const breadcrumbs = [];
  const router = useRouter();
  breadcrumbs.push({ title: t('title'), link: contactPath() });
  const formId =
    router.locale === defaultLocale
      ? 'e74285ae-7f20-40c8-a937-bc4bb931be84'
      : 'b9caac3c-d1ac-4ad8-bf28-0106eea837a0';

  return (
    <MainDocument
      title={t('title')}
      description={t('description')}
      linkCanonical={webPath(contactPath(), router.locale)}
    >
      <ServicePageTemplate header={<Header viewer={viewer} />} breadcrumbs={[]}>
        <Container>
          <Main>
            <Content>
              <TitleContainer>
                <Title variant="h1">{t('title')}</Title>
              </TitleContainer>
              <Description>{t('description')}</Description>
              <FaqContainer>
                <FaqList>
                  <FaqListItem>
                    <FaqTitleContainer>
                      <FaqTitleIcon name="check" />
                      <FaqTitle>{t('faq_title_1')}</FaqTitle>
                    </FaqTitleContainer>
                    <FaqText>{t('faq_text_1')}</FaqText>
                  </FaqListItem>
                  <FaqListItem>
                    <FaqTitleContainer>
                      <FaqTitleIcon name="check" />
                      <FaqTitle>{t('faq_title_2')}</FaqTitle>
                    </FaqTitleContainer>
                    <FaqText>{t('faq_text_2')}</FaqText>
                  </FaqListItem>
                  <FaqListItem>
                    <FaqTitleContainer>
                      <FaqTitleIcon name="check" />
                      <FaqTitle>{t('faq_title_3')}</FaqTitle>
                    </FaqTitleContainer>
                    <FaqTextList>
                      <FaqTextListItem>- {t('faq_text_3.0')}</FaqTextListItem>
                    </FaqTextList>
                    <FaqTextList>
                      <FaqTextListItem>- {t('faq_text_3.1')}</FaqTextListItem>
                    </FaqTextList>
                    <FaqTextList>
                      <FaqTextListItem>- {t('faq_text_3.2')}</FaqTextListItem>
                    </FaqTextList>
                  </FaqListItem>
                  <FaqListItem>
                    <FaqTitleContainer>
                      <FaqTitleIcon name="check" />
                      <FaqTitle>{t('faq_title_4')}</FaqTitle>
                    </FaqTitleContainer>
                    <FaqText>{t('faq_text_4')}</FaqText>
                  </FaqListItem>
                  <FaqListItem>
                    <FaqTitleContainer>
                      <FaqTitleIcon name="check" />
                      <FaqTitle>{t('faq_title_5')}</FaqTitle>
                    </FaqTitleContainer>
                    <FaqTextList>
                      <FaqTextListItem>- {t('faq_text_5.0')}</FaqTextListItem>
                    </FaqTextList>
                    <FaqTextList>
                      <FaqTextListItem>- {t('faq_text_5.1')}</FaqTextListItem>
                    </FaqTextList>
                    <FaqTextList>
                      <FaqTextListItem>- {t('faq_text_5.2')}</FaqTextListItem>
                    </FaqTextList>
                    <FaqTextList>
                      <FaqTextListItem>- {t('faq_text_5.3')}</FaqTextListItem>
                    </FaqTextList>
                    <FaqTextList>
                      <FaqTextListItem>- {t('faq_text_5.4')}</FaqTextListItem>
                    </FaqTextList>
                    <FaqTextList>
                      <FaqTextListItem>- {t('faq_text_5.5')}</FaqTextListItem>
                    </FaqTextList>
                  </FaqListItem>
                </FaqList>
              </FaqContainer>
              <CompanyContainer>
                <ContactCompanyList />
              </CompanyContainer>
            </Content>
          </Main>
          <Sub>
            {router.locale && (
              <FormContainer
                dangerouslySetInnerHTML={{
                  __html: `
    <div class="jicoo-widget" data-url="${pricingEducationWidgetPath(
      router.locale,
    )}" style="min-width:320px;height:720px;box-sizing:content-box;"></div>
    <script type="text/javascript" src="https://www.jicoo.com/widget/event_type.js" async></script>
              `,
                }}
              />
            )}
          </Sub>
        </Container>
      </ServicePageTemplate>
    </MainDocument>
  );
}

const Container = styled.div`
  display: flex;
  margin: 0 auto;
  max-width: 1080px;
  ${mediaMax.tablet} {
    display: block;
    padding: 0 ${spacing(12)};
  }
`;
const Main = styled.div`
  width: 50%;
  ${mediaMax.tablet} {
    width: 100%;
  }
`;
const Content = styled.div`
  margin: ${spacing(32)} 0;
  padding-right: ${spacing(48)};
  ${mediaMax.tablet} {
    padding-right: 0;
  }
`;

const TitleContainer = styled.div`
  margin-bottom: ${spacing(32)};
`;
const Title = styled(Headline)`
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(24)};
`;
const TitleSub = styled.div`
  font-weight: ${fontWeight('bold')};
  color: ${primary};
`;
const Description = styled.div`
  margin-bottom: ${spacing(32)};
  white-space: pre-wrap;
`;
const FaqContainer = styled.div`
  background-color: ${blueGrayPale};
  border-radius: ${borderRadius(16)};
  padding: ${spacing(16)};
  margin-bottom: ${spacing(32)};
`;
const Sub = styled.div`
  width: 50%;
  padding: ${spacing(24)} 0;
  ${mediaMax.tablet} {
    width: 100%;
  }
`;
const FormContainer = styled.div`
  /* border: 1px solid ${gray04}; */
  /* border-radius: ${borderRadius(16)}; */
  /* padding: ${spacing(20)}; */
  ${mediaMax.tablet} {
    width: 100%;
  }
`;

const CompanyContainer = styled.div`
  margin-bottom: ${spacing(32)};
`;

const FaqList = styled.div``;
const FaqListItem = styled.div`
  border-bottom: 1px solid ${gray04};
  padding-bottom: ${spacing(16)};
  margin-bottom: ${spacing(16)};
  :last-of-type {
    border: 0;
    margin-bottom: 0;
  }
`;
const FaqTitleContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${spacing(8)};
`;
const FaqIntegrationContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
`;
const FaqIntegrationImage = styled(Image)`
  margin-right: ${spacing(20)};
  margin-bottom: ${spacing(8)};
  height: auto;
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(8)};
  }
`;
const FaqTitle = styled.div`
  font-weight: ${fontWeight('bold')};
`;
const FaqTitleIcon = styled(Icon)`
  color: ${primary};
  margin-right: ${spacing(4)};
  width: 20px !important;
  height: 20px !important;
`;
const FaqText = styled.div``;
const FaqTextList = styled.ul``;
const FaqTextListItem = styled.li``;
